/**
 * Proposal Detail Page
 *
 * Story FOS-4.2.8: ProposalList & Detail Enhancement
 * Task 7: Create ProposalDetailPage
 *
 * Shows full proposal details with vote breakdown.
 * Navigated to from ProposalCard click.
 *
 * @see AC-4.2.8.5 - Full proposal description and vote breakdown displayed
 * @see AC-4.2.8.6 - Vote Now navigates to VotingPage
 *
 * @module pages/Governance/ProposalDetailPage
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, ArrowLeft } from 'lucide-react';
import { useAuth } from '@hello-world-dao/auth';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { ProposalDetail, ProposalComments, AiProposalSummary } from '../../components/governance';
import {
  getProposalDetails,
  getVoteBreakdown,
  hasUserVoted,
  getProposalAiSummary,
  isGovernanceError,
  GovernanceErrorType,
  type ProposalDetails,
  type VoteBreakdown,
} from '../../services/governanceService';

// =============================================================================
// Component
// =============================================================================

/**
 * Proposal detail page
 */
export function ProposalDetailPage() {
  const { proposalId } = useParams<{ proposalId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Data state
  const [proposal, setProposal] = useState<ProposalDetails | null>(null);
  const [breakdown, setBreakdown] = useState<VoteBreakdown | null>(null);
  const [userHasVoted, setUserHasVoted] = useState(false);
  const [aiSummary, setAiSummary] = useState<string | null>(null);
  const [aiSummaryLoading, setAiSummaryLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Parse proposal ID
  const parsedId = proposalId ? parseInt(proposalId, 10) : null;

  // ==========================================================================
  // Data Fetching
  // ==========================================================================

  const loadData = useCallback(async () => {
    if (!parsedId || isNaN(parsedId)) {
      setError('Proposal not found');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const [proposalData, breakdownData, votedData, aiSummaryData] = await Promise.all([
        getProposalDetails(parsedId),
        getVoteBreakdown(parsedId),
        user?.icPrincipal
          ? hasUserVoted(parsedId, user.icPrincipal)
          : Promise.resolve({ hasVoted: false }),
        getProposalAiSummary(parsedId).catch(() => ({ summary: null })),
      ]);

      setProposal(proposalData);
      setBreakdown(breakdownData);
      setUserHasVoted(votedData.hasVoted);
      setAiSummary(aiSummaryData.summary);
      setAiSummaryLoading(false);
    } catch (e) {
      // Check if it's a real governance error (not network)
      if (isGovernanceError(e) && e.type !== GovernanceErrorType.NETWORK_ERROR) {
        if (e.type === GovernanceErrorType.NOT_FOUND) {
          setError('Proposal not found');
        } else {
          setError(e.message);
        }
        setIsLoading(false);
        return;
      }

      // No data available when API is not reachable
      setError('Proposal not found');
      setAiSummaryLoading(false);
      setIsLoading(false);
      return;
    } finally {
      setIsLoading(false);
      setAiSummaryLoading(false);
    }
  }, [parsedId, user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ==========================================================================
  // Render
  // ==========================================================================

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="proposal-detail-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Error state
  if (error || !proposal || !breakdown) {
    return (
      <div className="py-8" data-testid="proposal-detail-error">
        <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <div>
            <p className="font-medium text-red-700 dark:text-red-300">
              {error || 'Failed to load proposal'}
            </p>
            <button
              onClick={() => navigate('/proposals')}
              className="text-sm text-red-600 dark:text-red-400 hover:underline mt-1"
            >
              Return to proposals
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div data-testid="proposal-detail-page">
        {/* Back link */}
        <button
          onClick={() => navigate('/proposals')}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to proposals
        </button>

        {/* Proposal Detail */}
        <ProposalDetail
          id={proposal.id}
          title={proposal.title}
          category={proposal.category}
          description={proposal.description}
          proposerName={proposal.proposer_name}
          createdAt={proposal.created_at}
          votingEndsAt={proposal.voting_ends_at}
          status={proposal.status}
          yesVotes={breakdown.yes}
          noVotes={breakdown.no}
          abstainVotes={breakdown.abstain}
          needsRefinementVotes={breakdown.needs_refinement}
          totalVotes={breakdown.total_votes}
          quorumThreshold={breakdown.quorum_threshold}
          userHasVoted={userHasVoted}
        />

        {/* AI Proposal Summary (BL-045.2) */}
        <AiProposalSummary summary={aiSummary} isLoading={aiSummaryLoading} />

        {/* Proposal Comments (BL-024.2) */}
        <ProposalComments proposalId={parsedId!} />
      </div>
    </ErrorBoundary>
  );
}

export default ProposalDetailPage;
